import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Layout } from "../components/layout/Layout";
import { Section, SectionHeader } from "../components/common/Section";
import { FaTrophy, FaUsers, FaGlobe, FaHeart, FaArrowRight } from "react-icons/fa";

const About = () => {
  const values = [
    {
      icon: FaTrophy,
      title: "Excellence",
      description: "We pursue excellence in every product, never compromising on quality or performance.",
    },
    {
      icon: FaUsers,
      title: "Partnership",
      description: "We build lasting relationships with our clients, growing together as true partners.",
    },
    {
      icon: FaGlobe,
      title: "Innovation",
      description: "We continuously innovate, staying ahead of industry trends and technology.",
    },
    {
      icon: FaHeart,
      title: "Integrity",
      description: "We conduct business with honesty, transparency, and unwavering ethical standards.",
    },
  ];

  const timeline = [
    {
      year: "Foundation",
      title: "Company Established",
      description: "Crown Amusement was founded with a vision to revolutionize the skill gaming industry.",
    },
    {
      year: "Growth",
      title: "Manufacturing Expansion",
      description: "Expanded our manufacturing capabilities with state-of-the-art facilities.",
    },
    {
      year: "Innovation",
      title: "Software Development",
      description: "Launched our in-house software development division for custom gaming solutions.",
    },
    {
      year: "Today",
      title: "Industry Leader",
      description: "Now a leading name in skill gaming, serving clients nationwide with premium products.",
    },
  ];

  return (
    <Layout>
      <Helmet>
        <title>About Crown Amusement | Trusted Skill Game Manufacturer & Distributor</title>
        <meta name="description" content="Crown Amusement is a trusted manufacturer and distributor of premium skill game cabinets, fish games, and online gaming solutions. Learn about our mission, values, and commitment to the amusement industry." />
        <link rel="canonical" href="https://crownamusement.com/about" />
        <meta property="og:title" content="About Crown Amusement | Skill Game Industry Leaders" />
        <meta property="og:description" content="Trusted manufacturer and distributor of premium skill game cabinets, fish games, and online gaming solutions." />
        <meta property="og:url" content="https://crownamusement.com/about" />
      </Helmet>
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1673512703111-c38c42a1f1a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBkYXJrJTIwY2FzaW5vJTIwaW50ZXJpb3IlMjBnb2xkJTIwbGlnaHRpbmd8ZW58MHx8fHwxNzc0MTU2NTU3fDA&ixlib=rb-4.1.0&q=85"
            alt="About Crown Amusement"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-obsidian via-obsidian/95 to-obsidian" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-4 py-1.5 text-xs font-medium text-gold-400 bg-gold-500/10 rounded-full mb-6 tracking-wider uppercase border border-gold-500/20">
              About Us
            </span>
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-white">
              The Story Behind{" "}
              <span className="text-gold-gradient">Crown Amusement</span>
            </h1>
            <p className="mt-6 text-gray-300 text-lg leading-relaxed">
              We are passionate about creating exceptional gaming experiences through 
              premium manufacturing, innovative software, and dedicated service.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission */}
      <Section background="surface">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block px-4 py-1.5 text-xs font-medium text-gold-400 bg-gold-500/10 rounded-full mb-4 tracking-wider uppercase">
              Our Mission
            </span>
            <h2 className="font-heading text-4xl text-white font-semibold">
              Elevating Entertainment Through Innovation
            </h2>
            <p className="mt-6 text-gray-400 leading-relaxed">
              At Crown Amusement, our mission is to deliver premium skill gaming solutions 
              that exceed expectations. We combine cutting-edge technology with superior 
              craftsmanship to create products that entertain, engage, and endure.
            </p>
            <p className="mt-4 text-gray-400 leading-relaxed">
              Whether you&apos;re looking to buy, sell, or lease gaming machines, we provide 
              comprehensive solutions tailored to your business needs. Our commitment to 
              quality and customer satisfaction drives everything we do.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="aspect-square rounded-3xl overflow-hidden border border-white/10">
              <img
                src="https://images.pexels.com/photos/5767455/pexels-photo-5767455.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Gaming experience"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-obsidian/80 to-transparent" />
            </div>
          </motion.div>
        </div>
      </Section>

      {/* Values */}
      <Section background="default">
        <SectionHeader
          label="Our Values"
          title="What Drives Us"
          description="The core principles that guide our work and relationships."
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {values.map((value, index) => (
            <motion.div
              key={value.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              viewport={{ once: true }}
              className="p-8 rounded-2xl bg-surface border border-white/5 hover:border-gold-500/30 transition-all duration-300 group"
              data-testid={`value-card-${index}`}
            >
              <div className="w-14 h-14 rounded-xl bg-gold-500/10 flex items-center justify-center mb-6 group-hover:bg-gold-500/20 transition-colors">
                <value.icon className="text-2xl text-gold-500" />
              </div>
              <h3 className="font-heading text-xl text-white mb-3">{value.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{value.description}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Timeline */}
      <Section background="gradient">
        <SectionHeader
          label="Our Journey"
          title="Building a Legacy"
          description="Key milestones in our journey to becoming an industry leader."
        />

        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gold-500/20 hidden lg:block" />

          <div className="space-y-12 lg:space-y-0">
            {timeline.map((item, index) => (
              <motion.div
                key={item.year}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`lg:flex items-center gap-8 ${
                  index % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"
                }`}
                data-testid={`timeline-${index}`}
              >
                <div className={`lg:w-5/12 ${index % 2 === 0 ? "lg:text-right" : "lg:text-left"}`}>
                  <div className="p-6 rounded-2xl bg-white/5 border border-white/10">
                    <span className="font-accent text-gold-500 text-sm font-bold">{item.year}</span>
                    <h4 className="font-heading text-xl text-white mt-2">{item.title}</h4>
                    <p className="text-gray-400 text-sm mt-2">{item.description}</p>
                  </div>
                </div>
                <div className="hidden lg:flex w-2/12 justify-center">
                  <div className="w-4 h-4 rounded-full bg-gold-500 border-4 border-obsidian" />
                </div>
                <div className="lg:w-5/12" />
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* CTA */}
      <Section background="surface">
        <div className="text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-4xl md:text-5xl text-white font-semibold"
          >
            Let&apos;s Work Together
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            viewport={{ once: true }}
            className="mt-6 text-gray-400 text-lg max-w-2xl mx-auto"
          >
            Join the growing family of businesses that trust Crown Amusement for their gaming needs.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
            className="mt-10 flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link
              to="/contact"
              data-testid="about-contact-cta"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-gold-600 to-gold-500 text-obsidian font-bold rounded-full hover:scale-105 transition-transform shadow-gold-glow"
            >
              Contact Us
              <FaArrowRight />
            </Link>
            <Link
              to="/products"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-gold-500/30 text-gold-400 font-semibold rounded-full hover:bg-gold-500/10 transition-colors"
            >
              View Products
            </Link>
          </motion.div>
        </div>
      </Section>
    </Layout>
  );
};

export default About;
